// 3.3 Playground on its own page (owner, 2026-10-17): a VS Code window on a real Node container. The agent
// (firstfund-lender-hub/agent) starts one hub-sandbox container per batch; this page shows its files, runs endpoints
// in it, opens a real shell in it, and commits in it. Save = one commit (+ discovery → the manifest); Run = Ctrl+Enter;
// a built or live version is read-only. Offline (no agent): the store's copy is shown read-mostly, nothing runs.
import { apiMode } from '@/lib/hubApi'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Code2, Files, FlaskConical, GitBranch, GraduationCap, Hammer, Keyboard, Moon, Play, PlayCircle, Plus, RefreshCw, Rocket, Save, ScanSearch, Search, Send, Settings, Sun, X } from 'lucide-react'
import { ConfirmDialog, PromptDialog, Tooltip, applyColourScheme, initials, notify } from '@/kit/ui'
import { TEST_APPLICANT } from '@/fixtures/code'
import { runTestEvent, type TreeEntry, agentStartHint } from '@/lib/agent'
import { STATUS_LABEL, STRIP, batchName, canBuild, canMakeLive, codeKeyOf, compareVersions, fileTree, isEditable, isValidVersion, nextStep, nextVersion, ofCode, sameFiles, testsJson } from '@/lib/batch'
import { KEEP_FILE, endpointLabel, endpointTemplate, folderOf, foldersOf, helperTemplate, isValidPath, renamePath, routeOfFile, routeOfTestsPath, testsPathOf } from '@/lib/endpoints'
import { isApiPath, staticDiscover } from '@/lib/manifest'
import { countOf, layoutProblems, resultProblems, type Problem } from '@/lib/problems'
import { DEMO_PREV } from '@/lib/sandbox'
import { useCodeStore } from '@/store/codeStore'
import { useDevStore } from '@/store/devStore'
import { useHubStore } from '@/store/hubStore'
import type { CodeFile, Lender, Manifest, TestEvent, TestResult } from '@/types'
import { Group, Handle, Pane } from '@/screens/shared/Resizable'
import { useSandbox } from '../../useSandbox'
import { CodeEditor, type EditorMarker } from '../CodeEditor'
import { TestEventDrawer } from '../TestEventDrawer'
import { useBatchActions } from '../useBatchActions'
import { BottomPanel, type PanelTab } from './BottomPanel'
import { Explorer, dotOf } from './Explorer'
import { NewFileDialog, type NewFileKind } from './NewFileDialog'
import { SearchView, SourceControlView, TestingView } from './SideViews'
import { ApiClient, ApiClientView, CLIENT_TAB, type ClientResult, type ClientTarget } from './ApiClient'
import { type ClientVar, HTTP_TAB, HttpClient, HttpSideView } from './HttpClient'
import { type HistoryItem, type HttpDraft, type SavedHttpRequest, draftOfSaved, emptyDraft, fileText, importPostman, ipCheckDraft, keysRemoved, toSpec } from '@/lib/httpClient'
import { httpCollections } from '@/lib/httpCollections'
import type { HttpAnswer, HttpRequest } from '@/lib/agent'
import { type Draft, type Parsed, type SavedRequest, draftFrom, draftOf, requests, tryOnContainer } from '@/lib/apiClient'
import { DEFAULT_HUB_URLS } from '@/lib/urls'
import { useSettingsStore } from '@/store/settingsStore'

type View = 'explorer' | 'search' | 'testing' | 'git' | 'client'
const stamp = () => new Date().toLocaleTimeString('en-IN', { hour12: false })
const short = (v: unknown, n = 160) => {
  const s = JSON.stringify(v)
  return s && s.length > n ? s.slice(0, n) + '…' : s
}
const newTestId = () => `t_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`
const firstOpen = (files: CodeFile[], manifest: Manifest) => manifest.endpoints[0]?.file ?? files.find((f) => f.path.endsWith('.js'))?.path ?? files[0]?.path ?? ''
/** files of the sandbox the editor shows: text, not generated, not the keys */
const shown = (e: TreeEntry) => e.kind === 'file' && !e.path.startsWith('tests/') && !e.path.startsWith('.hub/') && e.path !== '.env' && e.path !== '.gitignore' && !/\.(png|jpg|gif|ico|woff2?|tgz|zip|lock)$/.test(e.path) && (e.size ?? 0) < 400_000
/** The playground's keyboard shortcuts (the keyboard button in the title bar lists them). */
export const KEYS: [string, string][] = [
  ['Ctrl+B', 'Show or hide the side bar'],
  ['Ctrl+Shift+E', 'Explorer (the files)'],
  ['Ctrl+Shift+F', 'Search in the files'],
  ['Ctrl+Shift+G', 'Source control'],
  ['Ctrl+J', 'Show or hide the bottom panel'],
  ['Ctrl+`', 'Terminal'],
  ['Ctrl+Enter', 'Run the open endpoint’s test events'],
  ['Ctrl+Shift+Enter', 'Run all tests'],
  ['Ctrl+S', 'Save (one commit)'],
]

export function Playground({ lender }: { lender: Lender }) {
  const [params, setParams] = useSearchParams()
  const batches = useCodeStore((s) => s.batches)
  const captured = useCodeStore((s) => s.captured)
  const saveToStore = useCodeStore((s) => s.save)
  const setManifestInStore = useCodeStore((s) => s.setManifest)
  const users = useHubStore((s) => s.users)
  const liveLender = useHubStore((s) => s.lenders.find((l) => l.slug === lender.slug)) ?? lender
  const { colourScheme, setColourScheme, setCurrentUser } = useDevStore()
  // BP-1: `p` names the product whose batch this is (`lender` = the whole-lender batch); left out = any of the lender's
  const productParam = params.get('p')
  const mine = useMemo(
    () => (productParam === null ? batches.filter((b) => b.lenderSlug === lender.slug) : ofCode(batches, lender.slug, productParam === 'lender' ? null : productParam)).sort((a, b) => compareVersions(b.version, a.version)),
    [batches, lender.slug, productParam],
  )
  const selected =
    mine.find((b) => b.version === params.get('v')) ??
    mine.find((b) => b.status === 'draft' || b.status === 'tests_passed') ??
    mine.find((b) => b.status === 'built') ??
    mine.find((b) => b.status === 'live') ??
    mine[0]
  const { user, can, busy, runAll, build, makeLive, newVersion } = useBatchActions()
  const [environment, setEnvironment] = useState<'uat' | 'production'>('uat')
  const sb = useSandbox(liveLender, selected, { environment })

  // the working copy (the sandbox's files once it is open) and the window
  const [files, setFiles] = useState<CodeFile[]>([])
  const [extras, setExtras] = useState<{ path: string; count?: number }[]>([])
  const [tests, setTests] = useState<TestEvent[]>([])
  const [manifest, setManifest] = useState<Manifest>(() => staticDiscover([]))
  const [message, setMessage] = useState('')
  const [results, setResults] = useState<Record<string, TestResult>>({})
  const [tabs, setTabs] = useState<string[]>([])
  const [view, setView] = useState<View>('explorer')
  const [sideOpen, setSideOpen] = useState(true)
  const [panel, setPanel] = useState<{ open: boolean; tab: PanelTab }>({ open: true, tab: 'console' })
  const [output, setOutput] = useState<string[]>([])
  const [consoleLines, setConsoleLines] = useState<string[]>(['Ready. Pick an endpoint file and press Run (Ctrl+Enter), or open Testing.'])
  const [gitChanges, setGitChanges] = useState<{ mark: string; path: string }[] | null>(null)
  const [cursor, setCursor] = useState({ line: 1, col: 1 })
  const [markers, setMarkers] = useState<Record<string, EditorMarker[]>>({})
  const [jump, setJump] = useState<{ line: number; n: number } | undefined>()
  const [source, setSource] = useState<'sample' | 'real'>('sample')
  const [running, setRunning] = useState<string | null>(null)
  const [newFile, setNewFile] = useState<string | null>(null)
  const [newFolder, setNewFolder] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [newTestFor, setNewTestFor] = useState<string | null>(null)
  const [confirmLive, setConfirmLive] = useState(false)
  const [confirmProd, setConfirmProd] = useState(false)
  const [confirmReseed, setConfirmReseed] = useState(false)
  const [newVersionOpen, setNewVersionOpen] = useState(false)
  // the API client (PG-1): the request open in its tab, the target, the last answer, the lender's saved requests
  const [clientDraft, setClientDraft] = useState<Draft | null>(null)
  const [clientTarget, setClientTarget] = useState<ClientTarget>('sandbox')
  const [clientResult, setClientResult] = useState<ClientResult | null>(null)
  const [clientSending, setClientSending] = useState(false)
  const [saved, setSaved] = useState<SavedRequest[]>([])
  const [testFrom, setTestFrom] = useState<{ status: number; body: unknown; expectedOutcome: string | null } | null>(null)
  const hubUrls = useSettingsStore((s) => s.settings.hubUrls) ?? DEFAULT_HUB_URLS
  // the HTTP client (PG-2a): Postman for any address, sent from this lender's sandbox; the history lives in the page
  // PG-2c: one list (My APIs, the collection, the history); the 5th icon reopens the kind of request used last
  const [clientMode, setClientMode] = useState<'apis' | 'http'>('apis')
  const [httpDraft, setHttpDraft] = useState<HttpDraft | null>(null)
  const [httpAnswer, setHttpAnswer] = useState<HttpAnswer | null>(null)
  const [httpSending, setHttpSending] = useState(false)
  const [httpHistory, setHttpHistory] = useState<HistoryItem[]>([])
  const [httpCollection, setHttpCollection] = useState<SavedHttpRequest[]>([])
  const pending = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const dirtyRef = useRef(false)

  const active = params.get('file') ?? tabs[0] ?? ''
  const openFile = useCallback(
    (path: string, line?: number) => {
      setTabs((t) => (t.includes(path) ? t : [...t, path]))
      setParams((p) => (p.set('file', path), p), { replace: true })
      if (line) setJump({ line, n: Date.now() })
    },
    [setParams],
  )

  // the store's copy first; the sandbox's copy replaces it when the sandbox is ready
  useEffect(() => {
    if (!selected) return
    setFiles(selected.files)
    setTests(selected.tests)
    setManifest(selected.manifest)
    setExtras([])
    setMessage('')
    setResults({})
    setMarkers({})
    setGitChanges(null)
    const want = params.get('file')
    const first = want && (selected.files.some((f) => f.path === want) || want.startsWith('tests/')) ? want : firstOpen(selected.files, selected.manifest)
    setTabs(first ? [first] : [])
    if (first && first !== want) setParams((p) => (p.set('file', first), p), { replace: true })
  }, [selected?.id, selected?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const print = useCallback((lines: string[]) => setOutput((o) => [...o, ...lines]), [])

  const pullFromSandbox = useCallback(async () => {
    if (!sb.id || sb.status !== 'ready') return
    const tree = await sb.agent.files(sb.id)
    const texts = await Promise.all(tree.filter(shown).map(async (e) => ({ path: e.path, content: await sb.agent.read(sb.id!, e.path) })))
    setFiles(texts)
    setExtras(tree.filter((e) => e.closed && e.path !== '.git').map((e) => ({ path: e.path, count: e.count })))
    const st = await sb.agent.git(sb.id, 'status').catch(() => null)
    setGitChanges(st?.changes ?? null)
    print([`[${stamp()}] sandbox ${sb.info?.container ?? 'local'} ready · ${texts.length} files · ${sb.info?.runtime === 'docker' ? 'Docker container' : 'local Node on this PC'}`])
  }, [sb.id, sb.status]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sb.status === 'ready') void pullFromSandbox()
    // the console is the tab open by default: the reason goes there too, not only to Output
    const why = sb.status === 'offline'
      ? [`[${stamp()}] playground agent offline: ${sb.error}`, '  the store copy is shown; Run, Terminal and Discover need the agent']
      : sb.status === 'error' ? [`[${stamp()}] sandbox did not open: ${sb.error}`, '  Retry in the status bar; if it fails again, send this line to the developer'] : null
    if (why) {
      print(why)
      setConsoleLines((c) => [...c.filter((x) => !x.startsWith('Ready.')), ...why])
    }
  }, [sb.status]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let live = true
    requests.list(lender.slug).then((list) => live && setSaved(list), () => {})
    httpCollections.list(lender.slug).then((list) => live && setHttpCollection(list), () => {})
    return () => { live = false }
  }, [lender.slug])

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirtyRef.current) e.preventDefault() }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])

  // the working copy is read on the text as it is typed; the agent's hub-discover (Save, Discover) is what the store keeps
  // (a hook: before the early return below, so a batch that goes away does not change the hook order)
  const localManifest = useMemo(() => staticDiscover(files, manifest.generatedAt), [files, manifest.generatedAt])

  if (!selected) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas p-6 text-sm dark:bg-canvas-dark">
        <div className="card max-w-md text-center">
          No batch yet for {lender.name}. <Link className="text-primary underline" to={`/lenders/${lender.slug}/api/apis`}>Create API (3.1)</Link> makes the first one.
        </div>
      </div>
    )
  }

  const ready = sb.status === 'ready' && !!sb.id
  const canEdit = isEditable(selected) && can('code.edit')
  const dirty = !sameFiles(files, selected.files) || JSON.stringify(tests) !== JSON.stringify(selected.tests)
  dirtyRef.current = dirty
  const tree = fileTree(files, tests, localManifest.endpoints, extras)
  const testsRoute = routeOfTestsPath(active, localManifest)
  const activeFile = files.find((f) => f.path === active)
  const activeRoute = activeFile ? routeOfFile(activeFile) : null
  const editorValue = testsRoute ? testsJson(tests, testsRoute) : activeFile?.content ?? ''
  const envValues: Record<string, string | null> = Object.fromEntries(liveLender.envVars.map((v) => [v.name, environment === 'uat' ? v.uat : v.prod]))
  // LENDER_BASE_URL follows Lender info (the sandbox's .env gets it from there), whatever its row says
  const baseUrl = environment === 'uat' ? liveLender.env.uatBaseUrl : liveLender.env.prodBaseUrl
  if (baseUrl) envValues.LENDER_BASE_URL = baseUrl
  const problems: Problem[] = [
    ...Object.entries(markers).flatMap(([path, ms]) => ms.map((m) => ({ level: m.level, path, line: m.line, text: m.text, source: 'editor' as const }))),
    ...layoutProblems(files, localManifest, tests, envValues),
    ...resultProblems(Object.values(results).filter((r) => tests.some((t) => t.id === r.testId)), localManifest),
  ]
  const count = countOf(problems)
  const changed = (path: string): 'A' | 'M' | null => {
    const saved = selected.files.find((f) => f.path === path)
    if (!saved) return 'A'
    return saved.content !== files.find((f) => f.path === path)?.content ? 'M' : null
  }
  const fileOf = (route: string) => localManifest.endpoints.find((e) => e.path === route)?.file ?? files.find((f) => routeOfFile(f) === route)?.path ?? null

  const show = (tab: PanelTab) => setPanel({ open: true, tab })
  const say = (lines: string[]) => setConsoleLines((c) => [...c.filter((x) => !x.startsWith('Ready.')), ...lines])

  /** every change of the working copy: local state, then the sandbox (debounced per file) */
  const change = (nextFiles: CodeFile[], nextTests: TestEvent[] = tests) => {
    setFiles(nextFiles)
    setTests(nextTests)
  }
  const pushFile = (path: string, content: string) => {
    if (!ready) return
    const t = pending.current.get(path)
    if (t) clearTimeout(t)
    pending.current.set(path, setTimeout(() => { void sb.agent.write(sb.id!, path, content).catch((err: Error) => notify.error('Not written to the sandbox', err.message)); pending.current.delete(path) }, 400))
  }
  const onEdit = (content: string) => {
    if (!activeFile || testsRoute || !canEdit) return
    change(files.map((f) => (f.path === active ? { ...f, content } : f)))
    pushFile(active, content)
  }
  const flushPending = async () => {
    for (const [path, t] of pending.current) {
      clearTimeout(t)
      const f = files.find((x) => x.path === path)
      if (f && ready) await sb.agent.write(sb.id!, path, f.content)
    }
    pending.current.clear()
  }

  const save = async () => {
    if (!dirty || !canEdit) return
    if (ready) {
      try {
        await flushPending()
        for (const e of localManifest.endpoints) if (tests.some((t) => t.endpoint === e.path)) await sb.agent.write(sb.id!, testsPathOf(e.path), testsJson(tests, e.path))
        const c = await sb.agent.git(sb.id!, 'commit', { message: message.trim() || 'Update', author: { name: user.name, email: user.email } })
        const m = await sb.agent.discover(sb.id!).catch(() => null)
        if (m) setManifest(m)
        saveToStore(selected.id, files, tests, message, user.email, { sha: c.sha, manifest: m ?? undefined, commitFiles: c.files })
        print([`[${stamp()}] save · commit ${c.sha} “${c.message}” in the sandbox · ${(c.files ?? []).join(', ') || 'no change'}${m ? ` · manifest: ${m.endpoints.length} endpoint(s), ${m.env.length} variable(s)` : ''}`, '  the version is draft again: Run all tests before Build'])
        notify.success('Saved', `One commit (${c.sha}) in the git container, in your name.`)
        setGitChanges([])
      } catch (err) {
        notify.error('Save failed', err instanceof Error ? err.message : String(err))
        return
      }
    } else {
      saveToStore(selected.id, files, tests, message, user.email)
      const b = useCodeStore.getState().batches.find((x) => x.id === selected.id)!
      print([`[${stamp()}] save · commit ${b.commits[0]!.sha} “${b.commits[0]!.message}” (agent offline: recorded in the Hub only)`])
      notify.success('Saved', 'Recorded in the Hub; the sandbox will get it when the agent runs.')
    }
    setMessage('')
  }

  // one test event in full: the call the handler made, the reply used, the outcome, the system check
  const runTests = async (list: TestEvent[]): Promise<TestResult[]> => {
    if (!ready) { notify.error('Sandbox not ready', sb.error ?? 'The playground agent is not running.'); return [] }
    await flushPending()
    const out: TestResult[] = []
    show('console')
    for (const t of list) {
      const file = fileOf(t.endpoint)
      setRunning(t.id)
      const failed = (error: string): TestResult => ({ testId: t.id, endpoint: t.endpoint, name: t.name, expectedOutcome: t.expectedOutcome, actualOutcome: null, ok: false, log: [], error, value: undefined, ms: 0, issues: [] })
      // a call that throws (network, the agent gone) is a red test, never a Run button stuck on disabled
      const r: TestResult = file
        ? await runTestEvent(sb.agent, sb.id!, file, t, TEST_APPLICANT, { prev: DEMO_PREV, mode: source, environment }).catch((err: unknown) => failed(err instanceof Error ? err.message : String(err)))
        : failed(`no file for ${t.endpoint} (Discover endpoints)`)
      out.push(r)
      const v = r.value as { reply_code?: string; message?: string; lender_ref?: string } | undefined
      const known = v?.reply_code ? captured.find((c) => c.lenderSlug === lender.slug && c.code === v.reply_code) : undefined
      const lines = [`[${stamp()}] ${endpointLabel(t.endpoint)} · “${t.name}” · reply ${source === 'real' ? `from the real lender (${environment})` : 'injected from the sample'}`]
      if (r.call) lines.push(`  ctx.lender → ${r.call.method} ${r.call.url} · headers ${short(r.call.headers ?? {}, 120)} · body ${short(r.call.body ?? null, 140)}`)
      if (r.reply) lines.push(`  reply ${r.reply.status}${r.reply.error ? ` (${r.reply.error})` : ''} → ${r.actualOutcome ? `outcome ${r.actualOutcome}` : 'no outcome'}${v?.reply_code ? ` · reply_code ${v.reply_code}` : ''}${v?.lender_ref ? ` · lender_ref ${v.lender_ref}` : ''}${v?.message ? ` · “${v.message}”` : ''} · ${r.ms} ms`)
      for (const l of r.log) lines.push(`      ${l}`)
      for (const i of r.issues ?? []) lines.push(`  ${i.level === 'error' ? '✗' : '⚠'} system check: ${i.text}`)
      if (r.envMissing?.length) lines.push(`  ✗ missing variables: ${r.envMissing.join(', ')} (Env variables tab)`)
      if (r.error && !r.error.startsWith('Not approved')) lines.push(`  ✗ ${r.error}`)
      lines.push(r.ok ? `  ✓ approved: expected ${t.expectedOutcome}, got ${r.actualOutcome}${v?.reply_code ? ` · lender code ${v.reply_code} ${known ? (known.known === 'known' ? 'known' : 'captured, not yet known') : 'not captured yet'}` : ''}` : `  ✗ not approved: expected ${t.expectedOutcome}, got ${r.actualOutcome ?? 'nothing'}${(r.issues ?? []).some((i) => i.level === 'error') ? ', and the output does not match the system' : ''}`)
      say(lines)
      setResults((rs) => ({ ...rs, [t.id]: r }))
    }
    setRunning(null)
    return out
  }

  /** Run (Ctrl+Enter): the test events of the open endpoint, else every test event. */
  const runActive = () => {
    const route = testsRoute ?? activeRoute
    const list = route ? tests.filter((t) => t.endpoint === route) : tests
    if (!list.length) return void notify.info('Nothing to run', route ? 'This endpoint has no test events yet: Testing → + on it.' : isApiPath(activeFile?.path ?? '') ? 'This file defines no endpoint yet.' : 'Open an endpoint file (under src/api/) and press Run.')
    void runTests(list)
  }

  const runEverything = async () => {
    if (!ready) return void notify.error('Sandbox not ready', sb.error ?? 'The playground agent is not running.')
    if (!tests.length) return void notify.info('Nothing to run', 'No test events yet: Testing → + on an endpoint.')
    let out: Awaited<ReturnType<typeof runAll>>
    try {
      await flushPending()
      out = await runAll(selected, liveLender, { tests, record: !dirty, sandbox: sb.id!, fileOf, mode: source, environment })
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      notify.error('Run all failed', msg)
      say([`[${stamp()}] run all tests · ✗ ${msg}`])
      return show('console')
    }
    const { results: rs, lines } = out
    setResults(Object.fromEntries(rs.map((r) => [r.testId, r])))
    say([...lines, ...(dirty ? ['  (unsaved working copy: the result is not recorded on the version; Save first)'] : [])])
    if (lines.length) print([lines[0]!, lines.at(-1)!])
    show('console')
  }

  const discover = async () => {
    if (!ready) return void notify.error('Sandbox not ready', sb.error ?? 'The playground agent is not running.')
    await flushPending()
    try {
      const m = await sb.agent.discover(sb.id!)
      setManifest(m)
      setManifestInStore(selected.id, m, user.email)
      print([`[${stamp()}] hub-discover → .hub/manifest.json`, ...m.endpoints.map((e) => `  ${e.method} ${e.path}  ← ${e.file}  → ${e.outcomes.join(' · ')}`), ...m.env.map((v) => `  env ${v.name} ← ${v.files.join(', ')}`), ...m.problems.map((p) => `  ✗ ${p.file}: ${p.text}`)])
      show('output')
      const fresh = m.env.filter((v) => !liveLender.envVars.some((x) => x.name === v.name))
      notify.success(`${m.endpoints.length} endpoint(s) found`, fresh.length ? `New variables in tab 5: ${fresh.map((v) => v.name).join(', ')}` : `${m.env.length} variable(s), all known.`)
    } catch (err) {
      notify.error('Discover failed', err instanceof Error ? err.message : String(err))
    }
  }

  // ---- the API client (PG-1) ----
  /** Opens the API client's tab on an endpoint (a new request with the test applicant) or on a saved request. */
  const openClient = (route?: string | null, from?: SavedRequest) => {
    const target = from?.endpoint ?? route ?? clientDraft?.endpoint ?? activeRoute ?? localManifest.endpoints[0]?.path
    if (!target) return openHttp()
    setClientMode('apis')
    if (from) setClientDraft(draftOf(from))
    else if (route || !clientDraft) {
      const file = fileOf(target)
      const readsPrev = !!file && !!files.find((f) => f.path === file)?.content.includes('ctx.prev')
      setClientDraft(draftFrom(target, TEST_APPLICANT, readsPrev ? DEMO_PREV : null))
    }
    if (route || from) setClientResult(null)
    setTabs((t) => (t.includes(CLIENT_TAB) ? t : [...t, CLIENT_TAB]))
    setParams((p) => (p.set('file', CLIENT_TAB), p), { replace: true })
  }

  const sendClient = async (p: Parsed) => {
    if (!clientDraft) return
    const route = clientDraft.endpoint
    setClientSending(true)
    try {
      let r: ClientResult
      if (clientTarget === 'sandbox') {
        const file = fileOf(route)
        if (!file) throw new Error(`No file for ${route} in this version (Discover endpoints).`)
        if (!ready) throw new Error(sb.error ?? 'The sandbox is not ready yet.')
        await flushPending()
        const run = await sb.agent.run(sb.id!, { file, input: p.input, prev: p.prev, reply: p.reply, mode: p.reply ? 'sample' : 'real', environment, timeoutMs: 30000 })
        r = { target: 'sandbox', endpoint: route, environment, run, at: stamp(), typedReply: !!p.reply }
      } else {
        const t = await tryOnContainer(lender.slug, route, environment, p, selected?.productSlug ?? null)
        r = { target: 'container', endpoint: route, environment, run: t.result, at: stamp(), version: t.version, container: t.container, address: t.address }
      }
      setClientResult(r)
      // the Output keeps the outcome only: no input, no reply (they can hold a person's data)
      print([`[${stamp()}] API client · ${endpointLabel(route)} · ${r.target === 'sandbox' ? 'sandbox' : `${r.container}`} · ${environment} → ${r.run.outcome ?? 'no outcome'}${r.run.calls[0] ? ` (lender ${r.run.calls[0].reply.status || 'no reply'})` : ''} · ${r.run.ms} ms`])
    } catch (err) {
      notify.error('Not sent', err instanceof Error ? err.message : String(err))
    } finally {
      setClientSending(false)
    }
  }

  const saveClient = async (name: string, p: Parsed) => {
    if (!clientDraft) return
    try {
      const rec = await requests.save(lender.slug, { endpoint: clientDraft.endpoint, name, ...p }, user.email)
      setSaved((list) => [...list.filter((x) => x.id !== rec.id), rec].sort((a, b) => a.endpoint.localeCompare(b.endpoint) || a.name.localeCompare(b.name)))
      setClientDraft((d) => (d ? { ...d, name: rec.name, savedId: rec.id } : d))
      notify.success('Saved', `“${rec.name}” is under ${endpointLabel(rec.endpoint)} in the API client, for your team.`)
    } catch (err) {
      notify.error('Not saved', err instanceof Error ? err.message : String(err))
    }
  }

  const deleteSaved = async (r: SavedRequest) => {
    try {
      await requests.remove(r.id)
      setSaved((list) => list.filter((x) => x.id !== r.id))
      setClientDraft((d) => (d?.savedId === r.id ? { ...d, savedId: null, name: '' } : d))
    } catch (err) {
      notify.error('Not deleted', err instanceof Error ? err.message : String(err))
    }
  }

  // ---- the HTTP client (PG-2a) ----
  /** Opens the HTTP client's tab: on a given request (history, What is our IP?, New), else on the one open. */
  const openHttp = (d?: HttpDraft) => {
    setClientMode('http')
    if (d) { setHttpDraft(d); setHttpAnswer(null) }
    else if (!httpDraft) setHttpDraft(emptyDraft())
    setTabs((t) => (t.includes(HTTP_TAB) ? t : [...t, HTTP_TAB]))
    setParams((p) => (p.set('file', HTTP_TAB), p), { replace: true })
  }

  const sendHttp = async (req: HttpRequest, draft: HttpDraft) => {
    if (!ready) return void notify.error('Not sent', sb.error ?? 'The sandbox is not ready yet.')
    setHttpSending(true)
    setHttpAnswer(null)
    try {
      const a = await sb.agent.http(sb.id!, req)
      setHttpAnswer(a)
      setHttpHistory((h) => [{ at: stamp(), draft, status: a.ok ? a.status ?? null : null, ms: a.ms, error: a.ok ? null : a.error ?? null }, ...h].slice(0, 50))
      // the Output keeps the method, the host and the status: never the path, the headers or the body
      let host = ''
      try { host = new URL(req.url).host } catch { host = '?' }
      print([`[${stamp()}] HTTP client · ${req.method} ${host} → ${a.ok ? `${a.status} in ${a.ms} ms` : `no response (${a.code ?? 'error'})`} · from ${a.sentFrom ?? 'the sandbox'}`])
    } catch (err) {
      notify.error('Not sent', err instanceof Error ? err.message : String(err))
    } finally {
      setHttpSending(false)
    }
  }

  const sortHttp = (list: SavedHttpRequest[]) => [...list].sort((a, b) => a.folder.localeCompare(b.folder) || a.name.localeCompare(b.name))
  /** Save into the lender's collection: over the open saved one, or as a new one. The Hub empties typed keys. */
  const saveHttp = async (target: { asNew: boolean; folder: string; name: string }) => {
    if (!httpDraft) return
    const spec = toSpec(httpDraft)
    try {
      const it = !target.asNew && httpDraft.savedId
        ? await httpCollections.update(httpDraft.savedId, { folder: target.folder, name: target.name, request: spec }, user.email)
        : await httpCollections.create(lender.slug, target.folder, target.name, spec, user.email)
      setHttpCollection((list) => sortHttp([...list.filter((x) => x.id !== it.id), it]))
      // the draft keeps its files and the keys typed for Send; it is now the saved one
      setHttpDraft((d) => (d ? { ...d, name: it.name, folder: it.folder, savedId: it.id } : d))
      const removed = keysRemoved(spec, it.request)
      notify.success('Saved', `“${it.name}” is in ${lender.name}'s collection${it.folder ? ` / ${it.folder}` : ''}.${removed ? ` ${removed} typed key value${removed > 1 ? 's were' : ' was'} not saved: use a {{VARIABLE}} of the Env variables tab.` : ''}`)
    } catch (err) {
      notify.error('Not saved', err instanceof Error ? err.message : String(err))
    }
  }
  const deleteHttp = async (it: SavedHttpRequest) => {
    try {
      await httpCollections.remove(it.id)
      setHttpCollection((list) => list.filter((x) => x.id !== it.id))
      setHttpDraft((d) => (d?.savedId === it.id ? { ...d, savedId: null } : d))
    } catch (err) {
      notify.error('Not deleted', err instanceof Error ? err.message : String(err))
    }
  }
  /** A Postman collection file (v2.1) → this lender's collection, in its folders. */
  const importHttp = async (file: File) => {
    try {
      const p = importPostman(await fileText(file))
      if (!p.items.length) return void notify.info('Nothing to import', 'The collection has no requests.')
      const r = await httpCollections.import(lender.slug, file.name, p.items.map((i) => ({ ...i, folder: [p.name, i.folder].filter(Boolean).join('/').slice(0, 200) })), user.email)
      setHttpCollection(sortHttp(await httpCollections.list(lender.slug)))
      const missing = p.variables.filter((n) => !httpVars.some((v) => v.name === n))
      notify.success(`${r.imported} request${r.imported > 1 ? 's' : ''} imported`, [`Folder “${p.name}”.`, r.stripped ? `${r.stripped} typed key value${r.stripped > 1 ? 's' : ''} not kept: use {{VARIABLES}}.` : '', missing.length ? `Its variables ${missing.map((n) => `{{${n}}}`).join(', ')} are not in ${lender.name}'s Env variables yet: add them there.` : '', p.skipped.length ? `${p.skipped.length} skipped.` : ''].filter(Boolean).join(' '))
    } catch (err) {
      notify.error('Not imported', err instanceof Error ? err.message : String(err))
    }
  }

  // ---- explorer actions (the working copy and the sandbox; Save makes the commit) ----
  const exists = (path: string) => files.some((f) => f.path === path) || foldersOf(files).includes(path)
  const createFile = async (path: string, kind: NewFileKind, title: string) => {
    const content = kind === 'endpoint' ? endpointTemplate(title, 'POST', '/', 'bearer') : kind === 'helper' ? helperTemplate(path) : ''
    const folder = folderOf(path)
    const nextFiles = [...files.filter((f) => !(folder && f.path === `${folder}/${KEEP_FILE}`)), { path, content }]
    const route = kind === 'endpoint' ? routeOfFile({ path, content }) : null
    change(nextFiles, route ? [...tests, { id: newTestId(), endpoint: route, name: 'Reply 200 → ok', reply: { status: 200, body: { id: 'DEMO-1' } }, expectedOutcome: 'ok', source: 'sample' }] : tests)
    setNewFile(null)
    if (ready) await sb.agent.write(sb.id!, path, content).catch((err: Error) => notify.error('Not written to the sandbox', err.message))
    openFile(path)
    print([`[${stamp()}] new file ${path}${route ? ` · endpoint ${route} with one test event` : ''} (working copy; Save commits it)`])
  }
  const createFolder = async (path: string) => {
    change([...files, { path: `${path}/${KEEP_FILE}`, content: '' }])
    setNewFolder(null)
    if (ready) await sb.agent.write(sb.id!, `${path}/${KEEP_FILE}`, '').catch(() => {})
    print([`[${stamp()}] new folder ${path}/ (kept by ${KEEP_FILE} until a file goes in)`])
  }
  const rename = async (from: string, to: string) => {
    const r = renamePath(files, tests, from, to)
    change(r.files, r.tests)
    setTabs((t) => t.map((p) => (p === from ? to : p.startsWith(from + '/') ? to + p.slice(from.length) : p)))
    if (active === from || active.startsWith(from + '/')) setParams((p) => (p.set('file', active === from ? to : to + active.slice(from.length)), p), { replace: true })
    setRenaming(null)
    if (ready) await sb.agent.rename(sb.id!, from, to).catch((err: Error) => notify.error('Not renamed in the sandbox', err.message))
    print([`[${stamp()}] rename ${from} → ${to}; routes and test events follow. A workflow that calls the old route shows it in its checks.`])
  }
  const remove = async (path: string) => {
    const gone = files.filter((f) => f.path === path || f.path.startsWith(path + '/'))
    const goneRoutes = gone.map(routeOfFile).filter((r): r is string => !!r)
    change(files.filter((f) => !gone.includes(f)), tests.filter((t) => !goneRoutes.includes(t.endpoint)))
    setTabs((t) => t.filter((p) => !gone.some((g) => g.path === p)))
    if (gone.some((g) => g.path === active)) setParams((p) => (p.delete('file'), p), { replace: true })
    setDeleting(null)
    if (ready) await sb.agent.remove(sb.id!, path).catch((err: Error) => notify.error('Not deleted in the sandbox', err.message))
    print([`[${stamp()}] delete ${path}${gone.length > 1 ? ` (${gone.length} files)` : ''}${goneRoutes.length ? ` and the test events of ${goneRoutes.join(', ')}` : ''} (working copy; Save commits it)`])
  }

  const toggleScheme = () => {
    const next = colourScheme === 'dark' ? 'light' : 'dark'
    applyColourScheme(next)
    setColourScheme(next)
  }

  const next = nextStep(selected, dirty, can)
  const idx = STRIP.indexOf(selected.status)
  const breadcrumbs = testsRoute ? ['tests', active.slice(6)] : active.split('/')
  const language = active.endsWith('.md') ? 'Markdown' : active.endsWith('.json') ? 'JSON' : 'JavaScript'
  const sbLabel = sb.status === 'ready' ? `${sb.info?.runtime === 'docker' ? 'container' : 'local'} ready` : sb.status === 'opening' ? 'starting the sandbox…' : sb.status === 'offline' ? 'agent offline' : sb.status === 'error' ? 'did not open' : 'no sandbox'
  const changesList = gitChanges ?? [
    ...files.filter((f) => !selected.files.some((s) => s.path === f.path)).map((f) => ({ mark: 'A', path: f.path })),
    ...files.filter((f) => selected.files.some((s) => s.path === f.path && s.content !== f.content)).map((f) => ({ mark: 'M', path: f.path })),
    ...selected.files.filter((s) => !files.some((f) => f.path === s.path)).map((s) => ({ mark: 'D', path: s.path })),
  ]
  // keyboard shortcuts on the whole page, as in VS Code (KEYS lists them; the keyboard button shows the list). Inside the
  // editor Ctrl+S / Ctrl+Enter stay the editor's own commands. The ref keeps the handler on the latest state.
  const keys = useRef({ runActive, runEverything, save, show, canRun: false, canRunAll: false, canSave: false })
  keys.current = { runActive, runEverything, save, show, canRun: running === null && ready && can('code.test'), canRunAll: tests.length > 0 && busy !== selected.id && ready && can('code.test'), canSave: dirty && canEdit }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return
      const inEditor = e.target instanceof Element && !!e.target.closest('.monaco-editor')
      const k = e.key.toLowerCase()
      const k$ = keys.current
      const side = (id: View) => { setView(id); setSideOpen(true) }
      if (k === 'b' && !e.shiftKey) setSideOpen((o) => !o)
      else if (k === 'e' && e.shiftKey) side('explorer')
      else if (k === 'f' && e.shiftKey) side('search')
      else if (k === 'g' && e.shiftKey) side('git')
      else if (k === 'j' && !e.shiftKey) setPanel((p) => ({ ...p, open: !p.open }))
      else if (k === '`') k$.show('terminal')
      else if (k === 'enter' && e.shiftKey) { if (k$.canRunAll) void k$.runEverything() }
      else if (k === 'enter' && !inEditor) { if (k$.canRun) k$.runActive() }
      else if (k === 's' && !e.shiftKey && !inEditor) { if (k$.canSave) void k$.save() }
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  const [keysOpen, setKeysOpen] = useState(false)

  const activity: [View, typeof Files, string][] = [['explorer', Files, 'Explorer'], ['search', Search, 'Search'], ['testing', FlaskConical, 'Testing'], ['git', GitBranch, 'Source control'], ['client', Send, 'API client']]
  const clientOpen = active === CLIENT_TAB && clientDraft !== null
  const httpOpen = active === HTTP_TAB && httpDraft !== null
  // the lender's variables the HTTP client may use: names, and a value only when it is not a secret
  const httpVars: ClientVar[] = [
    { name: 'LENDER_BASE_URL', secret: false, value: baseUrl || null },
    ...liveLender.envVars.filter((v) => v.name !== 'LENDER_BASE_URL').map((v) => ({ name: v.name, secret: v.secret, value: v.secret ? null : (environment === 'production' ? v.prod : v.uat) ?? null })),
  ]

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white text-default dark:bg-slate-950" data-testid="playground">
      {/* title bar */}
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-slate-200 bg-slate-50 px-2 dark:border-slate-800 dark:bg-slate-900 [&_.btn]:whitespace-nowrap">
        <Tooltip content={`Back to ${lender.name} · 3.2 API batches`}>
          <Link to={`/lenders/${lender.slug}/api/batches`} className="icon-btn" aria-label={`Back to ${lender.name}`} onClick={(e) => { if (dirty && !window.confirm('Leave without saving? Your changes in the working copy are lost.')) e.preventDefault() }}><ArrowLeft size={16} /></Link>
        </Tooltip>
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-white"><Code2 size={15} /></span>
        <b className="hidden text-sm sm:inline">Playground</b>
        <label className="ml-1 flex min-w-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-950">
          <span className="truncate font-mono">{selected ? codeKeyOf(selected) : lender.slug}.batchApi</span>
          <select className="bg-transparent font-mono text-xs outline-none" aria-label="Batch version" value={selected.version} onChange={(e) => { if (!dirty || window.confirm('Open another version? Your unsaved changes are lost.')) setParams((p) => (p.set('v', e.target.value), p.delete('file'), p)) }}>
            {mine.map((b) => <option key={b.id} value={b.version}>{b.version} · {STATUS_LABEL[b.status]}</option>)}
          </select>
        </label>
        <span className="hidden truncate text-2xs text-muted lg:inline" data-testid="pg-saved">
          {dirty ? <span className="font-semibold text-amber-600 dark:text-amber-300">● unsaved changes</span> : <>saved · <code>{selected.git.commit}</code></>}
        </span>
        <div className="mx-2 hidden items-center gap-1 2xl:flex" data-testid="status-strip" title={next}>
          {STRIP.map((s, i) => (
            <span key={s} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-2xs ${i === idx ? 'bg-primary text-white' : i < idx ? 'bg-primary-soft text-primary-text' : 'bg-slate-200/70 text-muted dark:bg-slate-800'}`}>{STATUS_LABEL[s]}</span>
          ))}
          {idx < 0 && <span className="whitespace-nowrap rounded-full bg-slate-300 px-2 py-0.5 text-2xs dark:bg-slate-700">{STATUS_LABEL[selected.status]}</span>}
        </div>
        {/* narrower screens: only the current step */}
        <span className="hidden whitespace-nowrap rounded-full bg-primary px-2 py-0.5 text-2xs text-white sm:inline 2xl:hidden" title={next}>{STATUS_LABEL[selected.status]}</span>
        <div className="flex-1" />
        {can('code.publish') && (
          <select className={`input !min-h-7 w-auto py-0 text-2xs ${environment === 'production' ? '!border-red-500 !text-red-700 dark:!text-red-300' : ''}`} aria-label="Environment" value={environment} onChange={(e) => (e.target.value === 'production' ? setConfirmProd(true) : setEnvironment('uat'))} data-testid="pg-env">
            <option value="uat">UAT</option>
            <option value="production">Production</option>
          </select>
        )}
        {can('code.test') && (
          <>
            <Tooltip content={ready ? 'Run the test events of the open endpoint (Ctrl+Enter)' : 'Needs the sandbox'}><button type="button" className="btn btn-primary btn-sm" onClick={runActive} disabled={running !== null || !ready} data-testid="pg-run"><Play size={12} /> Run <span className="hidden text-2xs opacity-75 md:inline">Ctrl+Enter</span></button></Tooltip>
            <Tooltip content={ready ? 'Run all tests (Ctrl+Shift+Enter)' : 'Needs the sandbox'}><button type="button" className="btn btn-secondary btn-sm" onClick={runEverything} disabled={!tests.length || busy === selected.id || !ready} data-testid="pg-run-all"><PlayCircle size={12} /> <span className="hidden md:inline">Run all</span></button></Tooltip>
            <Tooltip content={ready ? 'Discover endpoints (hub-discover): reads src/api/ and writes .hub/manifest.json (endpoints, outcomes, variables)' : 'Needs the sandbox'}><button type="button" className="btn btn-secondary btn-sm" onClick={discover} disabled={!ready} data-testid="pg-discover"><ScanSearch size={12} /> <span className="hidden lg:inline">Discover</span></button></Tooltip>
          </>
        )}
        <span className="mx-0.5 h-6 border-l border-slate-200 dark:border-slate-700" aria-hidden="true" />
        {can('code.edit') && <Tooltip content={dirty ? 'Save = one commit (Ctrl+S)' : 'Nothing to save'}><button type="button" className="btn btn-secondary btn-sm" onClick={() => void save()} disabled={!dirty || !canEdit} data-testid="pg-save"><Save size={12} /> <span className="hidden md:inline">Save</span></button></Tooltip>}
        {can('code.publish') && (
          <>
            <Tooltip content={canBuild(selected, can, dirty) ? 'npm ci, tests, discover, image + bundle with checksum; the version is fixed' : dirty ? 'Save first' : selected.status === 'draft' ? 'After Run all tests is green' : 'Already built'}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!canBuild(selected, can, dirty) || busy === selected.id} onClick={async () => { print(await build(selected)); show('output') }} data-testid="pg-build"><Hammer size={12} /> <span className="hidden md:inline">Build</span></button>
            </Tooltip>
            <Tooltip content={canMakeLive(selected, can) ? 'The runner starts the image; live workflows move after the C1 check' : selected.status === 'live' ? 'Already live' : 'Build first'}>
              <button type="button" className="btn btn-accent btn-sm" disabled={!canMakeLive(selected, can)} onClick={() => setConfirmLive(true)} data-testid="pg-make-live"><Rocket size={12} /> <span className="hidden md:inline">Make live</span></button>
            </Tooltip>
          </>
        )}
        <span className="mx-0.5 h-6 border-l border-slate-200 dark:border-slate-700" aria-hidden="true" />
        {can('code.edit') && <Tooltip content="New version from this"><button type="button" className="icon-btn" aria-label="New version from this" onClick={() => setNewVersionOpen(true)}><Plus size={16} /></button></Tooltip>}
        <span className="relative">
          <Tooltip content="Keyboard shortcuts"><button type="button" className="icon-btn" aria-label="Keyboard shortcuts" aria-expanded={keysOpen} onClick={() => setKeysOpen((o) => !o)} data-testid="pg-keys"><Keyboard size={16} /></button></Tooltip>
          {keysOpen && (
            <div className="absolute right-0 top-9 z-50 w-72 rounded-md border border-slate-200 bg-white p-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900" role="dialog" aria-label="Keyboard shortcuts" data-testid="pg-keys-list" onMouseLeave={() => setKeysOpen(false)}>
              <div className="mb-1 font-semibold">Keyboard shortcuts</div>
              <table className="w-full">
                <tbody>{KEYS.map(([k, what]) => <tr key={k}><td className="whitespace-nowrap py-0.5 pr-2"><kbd className="rounded border border-slate-300 px-1 font-mono text-2xs dark:border-slate-600">{k}</kbd></td><td className="py-0.5 text-muted">{what}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </span>
        {/* a new browser tab, so the working copy stays open */}
        <Tooltip content="Developer docs: the playground, the SDK, recipes (opens in a new tab)"><a href="/docs/playground" target="_blank" rel="noopener" className="icon-btn" aria-label="Developer docs" data-testid="pg-guide"><GraduationCap size={16} /></a></Tooltip>
        <Tooltip content={colourScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}><button type="button" className="icon-btn" onClick={toggleScheme} aria-label={colourScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>{colourScheme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}</button></Tooltip>
        <span className="flex items-center gap-1 border-l border-slate-200 pl-2 dark:border-slate-700">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-soft text-2xs font-bold text-primary-text" aria-hidden="true">{initials(user.name)}</span>
          {apiMode() ? <span className="hidden whitespace-nowrap text-2xs xl:block">{user.name} · {user.role}</span> : (
            <select className="hidden bg-transparent text-2xs outline-none sm:block" aria-label="Current user (dev switcher, no login yet)" value={user.id} onChange={(e) => setCurrentUser(e.target.value)}>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
            </select>
          )}
        </span>
      </header>
      {environment === 'production' && <div className="shrink-0 bg-red-600 px-3 py-1 text-center text-xs font-semibold text-white" data-testid="pg-prod-banner">PRODUCTION · real calls reach the lender with the production keys · recorded in History</div>}

      <div className="flex min-h-0 flex-1">
        {/* activity bar */}
        <nav className="flex w-12 shrink-0 flex-col items-center gap-1 border-r border-slate-200 bg-slate-100 py-2 dark:border-slate-800 dark:bg-slate-900" aria-label="Views">
          {activity.map(([id, Icon, label]) => (
            <button key={id} type="button" aria-label={label} title={label} aria-pressed={view === id && sideOpen} onClick={() => { if (view === id) return setSideOpen(!sideOpen); setView(id); setSideOpen(true); if (id === 'client') { if (clientMode === 'http') openHttp(); else openClient() } }} className={`relative flex h-10 w-10 items-center justify-center rounded ${view === id && sideOpen ? 'text-default' : 'text-muted hover:text-default'}`}>
              {view === id && sideOpen && <span className="absolute left-0 top-1.5 h-7 w-0.5 rounded bg-primary" />}
              <Icon size={20} />
              {id === 'testing' && Object.values(results).some((r) => !r.ok) && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-600" />}
              {id === 'git' && dirty && <span className="absolute right-1 top-1 rounded-full bg-primary px-1 text-[9px] font-bold text-white">●</span>}
            </button>
          ))}
          <div className="flex-1" />
          <Tooltip position="right" content={`Sandbox: ${sbLabel}${sb.info?.container ? ` · ${sb.info.container}` : ''} · ${environment}`}>
            <span className={`mb-1 h-2.5 w-2.5 rounded-full ${sb.status === 'ready' ? 'bg-emerald-500' : sb.status === 'opening' ? 'animate-pulse bg-amber-400' : 'bg-red-500'}`} aria-label={`Sandbox: ${sbLabel}`} />
          </Tooltip>
          <Tooltip position="right" content="Re-seed the sandbox from the Hub's copy (throws away uncommitted files in the sandbox)">
            <button type="button" className="flex h-10 w-10 items-center justify-center text-muted hover:text-default" aria-label="Re-seed the sandbox" onClick={() => setConfirmReseed(true)} disabled={sb.status === 'opening'}><RefreshCw size={17} /></button>
          </Tooltip>
          <Tooltip position="right" content="Runner settings: memory per container, time per lender call">
            <Link to="/settings/runner" className="flex h-10 w-10 items-center justify-center text-muted hover:text-default" aria-label="Runner settings"><Settings size={19} /></Link>
          </Tooltip>
        </nav>

        <Group id="playground" direction="horizontal" className="min-h-0 flex-1">
          {sideOpen && (
            <>
              <Pane id="pg-side" order={1} defaultSize={20} minSize={12} maxSize={45}>
                <aside className="h-full border-r border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60">
                  {view === 'explorer' && (
                    <Explorer
                      title={`${codeKeyOf(selected)}.batchApi ${selected.version}`}
                      tree={tree}
                      active={active}
                      changed={changed}
                      problems={problems}
                      editable={canEdit}
                      onOpen={openFile}
                      onNewFile={(folder) => setNewFile(folder)}
                      onNewFolder={(folder) => setNewFolder(folder)}
                      onRename={setRenaming}
                      onDelete={setDeleting}
                      activeFile={activeFile}
                      manifest={localManifest}
                      tests={tests}
                    />
                  )}
                  {view === 'search' && <SearchView files={files} onOpen={openFile} />}
                  {view === 'testing' && <TestingView manifest={localManifest} tests={tests} results={results} running={running} busy={busy === selected.id} editable={canEdit} canRun={ready} source={source} onSource={setSource} onRunOne={(t) => void runTests([t])} onRunEndpoint={(route) => void runTests(tests.filter((t) => t.endpoint === route))} onRunAll={runEverything} onAdd={setNewTestFor} onOpen={openFile} />}
                  {view === 'client' && (
                    <HttpSideView collection={httpCollection} history={httpHistory} current={httpOpen ? { id: httpDraft.id, savedId: httpDraft.savedId } : null} canEdit={can('code.edit')} onNew={() => openHttp(emptyDraft())} onIp={() => openHttp(ipCheckDraft())} onOpen={(h) => openHttp(h.draft)} onOpenSaved={(it) => openHttp(draftOfSaved(it))} onDelete={(it) => void deleteHttp(it)} onImport={(f) => void importHttp(f)} onClear={() => setHttpHistory([])}>
                      <ApiClientView version={selected.version} endpoints={localManifest.endpoints} saved={saved} current={clientOpen ? clientDraft : null} canDelete={can('code.test')} onOpen={(route) => openClient(route)} onOpenSaved={(r) => openClient(null, r)} onDelete={(r) => void deleteSaved(r)} />
                    </HttpSideView>
                  )}
                  {view === 'git' && <SourceControlView batch={selected} changes={changesList} message={message} onMessage={setMessage} onSave={() => void save()} canSave={dirty && canEdit} onOpen={openFile} />}
                </aside>
              </Pane>
              <Handle direction="horizontal" />
            </>
          )}
          <Pane id="pg-main" order={2} defaultSize={80} minSize={30}>
            <Group id="playground-editor" direction="vertical" className="h-full">
              <Pane id="pg-editor" order={1} defaultSize={panel.open ? 68 : 100} minSize={20}>
                <main className="flex h-full min-w-0 flex-col">
                  <div className="flex h-9 shrink-0 items-end overflow-x-auto border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900" role="tablist" aria-label="Open files">
                    {tabs.map((t) => {
                      const isClient = t === CLIENT_TAB
                      const isHttp = t === HTTP_TAB
                      const mark = t.startsWith('tests/') || isClient || isHttp ? null : changed(t)
                      return (
                        <div key={t} role="tab" aria-selected={t === active} className={`group flex h-9 shrink-0 cursor-pointer items-center gap-1.5 border-r border-slate-200 px-3 text-[12.5px] dark:border-slate-800 ${t === active ? 'border-t-2 border-t-primary bg-white dark:bg-slate-950' : 'text-muted hover:bg-white/60 dark:hover:bg-slate-950/60'}`} onClick={() => (isClient ? openClient() : isHttp ? openHttp() : openFile(t))}>
                          {isClient || isHttp ? <Send size={12} className={isHttp ? 'text-amber-600' : 'text-primary'} /> : <span className={`h-2 w-2 rounded-sm ${dotOf(t)}`} />}
                          <span className={t.startsWith('tests/') ? 'italic' : ''}>{isClient ? 'API client' : isHttp ? 'HTTP request' : t.split('/').pop()}</span>
                          {mark ? <span className="ml-1 h-2 w-2 rounded-full bg-slate-500 group-hover:hidden" title="unsaved" /> : null}
                          <button type="button" className={`icon-btn !p-0 ${mark ? 'hidden group-hover:inline-flex' : 'opacity-0 group-hover:opacity-100'}`} aria-label={`Close ${t}`} onClick={(e) => { e.stopPropagation(); const rest = tabs.filter((x) => x !== t); setTabs(rest); if (t === active) setParams((p) => (rest[0] ? p.set('file', rest[0]) : p.delete('file'), p), { replace: true }) }}><X size={13} /></button>
                        </div>
                      )
                    })}
                  </div>
                  {httpOpen ? (
                    <div className="min-h-0 flex-1">
                      <HttpClient
                        draft={httpDraft}
                        onDraft={setHttpDraft}
                        sending={httpSending}
                        answer={httpAnswer}
                        canSend={can('code.edit')}
                        canSave={can('code.edit')}
                        sandboxReady={ready}
                        environment={environment}
                        variables={httpVars}
                        endpoints={localManifest.endpoints}
                        canMakeTest={canEdit}
                        onSend={(r) => void sendHttp(r, httpDraft)}
                        onSave={(t) => void saveHttp(t)}
                        onMakeTestEvent={(endpoint, reply) => { setTestFrom({ ...reply, expectedOutcome: null }); setNewTestFor(endpoint) }}
                      />
                    </div>
                  ) : clientOpen ? (
                    <div className="min-h-0 flex-1">
                      <ApiClient
                        slug={lender.slug}
                        endpoints={localManifest.endpoints}
                        draft={clientDraft}
                        onDraft={setClientDraft}
                        target={clientTarget}
                        onTarget={setClientTarget}
                        environment={environment}
                        sandboxReady={ready}
                        containerAvailable={apiMode()}
                        sending={clientSending}
                        result={clientResult}
                        canSend={can('code.test')}
                        canSave={can('code.test')}
                        canMakeTest={canEdit}
                        hubBase={environment === 'production' ? hubUrls.production : hubUrls.uat}
                        lenderAddress={(environment === 'production' ? liveLender.env.prodBaseUrl : liveLender.env.uatBaseUrl) || null}
                        onSend={(p) => void sendClient(p)}
                        onSave={(name, p) => void saveClient(name, p)}
                        onMakeTestEvent={(reply, outcome) => { setTestFrom({ ...reply, expectedOutcome: outcome }); setNewTestFor(clientDraft.endpoint) }}
                      />
                    </div>
                  ) : (
                  <>
                  <div className="flex h-7 shrink-0 items-center gap-1 overflow-hidden px-3 text-[12px] text-muted" aria-label="Breadcrumb">
                    <span>{selected ? codeKeyOf(selected) : lender.slug}.batchApi</span>
                    {breadcrumbs.map((b, i) => <span key={i} className="flex items-center gap-1"><span aria-hidden="true">›</span><span className={i === breadcrumbs.length - 1 ? 'text-default' : ''}>{b}</span></span>)}
                    {activeRoute && <span className="flex items-center gap-1"><span aria-hidden="true">›</span><span className="text-violet-600 dark:text-violet-300">{localManifest.endpoints.find((e) => e.path === activeRoute)?.method ?? 'POST'} {activeRoute}</span></span>}
                  </div>
                  {!isEditable(selected) && (
                    <div className="shrink-0 border-y border-primary/30 bg-primary-soft/40 px-3 py-1 text-xs dark:bg-primary-soft/10" role="note" data-testid="read-only-banner">
                      <b>{batchName(selected)}</b> is <b>{STATUS_LABEL[selected.status]}</b> and read-only. To change the code: <button type="button" className="font-semibold text-primary underline" onClick={() => setNewVersionOpen(true)}>New version from this</button>.
                    </div>
                  )}
                  {isEditable(selected) && !can('code.edit') && <div className="shrink-0 bg-slate-100 px-3 py-1 text-xs text-muted dark:bg-slate-900">Read-only for {user.role}s: editors edit and test, admins build and make live.</div>}
                  {sb.status === 'offline' && <div className="shrink-0 bg-amber-50 px-3 py-1 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="pg-offline">Playground offline: this is the Hub's copy. Run, Terminal and Discover need a sandbox. {agentStartHint()} <button type="button" className="underline" onClick={() => void sb.open()}>Try again</button></div>}
                  {sb.status === 'ready' && sb.info?.stale && <div className="shrink-0 bg-amber-50 px-3 py-1 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="pg-stale">This sandbox holds <b>unsaved work from an older copy</b> of the batch (the Hub is at {selected.git.commit}). Save it (Source control), or Re-seed to take the Hub's copy.</div>}
                  {testsRoute && <div className="shrink-0 bg-violet-50 px-3 py-1 text-xs text-violet-800 dark:bg-violet-950/40 dark:text-violet-200">Generated from the test events of <b>{endpointLabel(testsRoute)}</b>: add or run them in <button type="button" className="underline" onClick={() => { setView('testing'); setSideOpen(true) }}>Testing</button>.</div>}
                  <div className="min-h-0 flex-1">
                    {active ? (
                      <CodeEditor
                        path={active}
                        value={editorValue}
                        readOnly={!canEdit || !!testsRoute || !activeFile}
                        onChange={onEdit}
                        onSave={() => void save()}
                        onRun={runActive}
                        onCursor={(line, col) => setCursor({ line, col })}
                        onMarkers={(ms) => setMarkers((m) => ({ ...m, [active]: ms }))}
                        jump={jump}
                        context={files.filter((f) => f.path !== active && /\.(c?js|mjs)$/.test(f.path) && !isApiPath(f.path))}
                        height="100%"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-muted">Open a file from the Explorer.</div>
                    )}
                  </div>
                  </>
                  )}
                </main>
              </Pane>
              {panel.open && (
                <>
                  <Handle direction="vertical" />
                  <Pane id="pg-panel" order={2} defaultSize={32} minSize={12}>
                    <BottomPanel
                      tab={panel.tab}
                      onTab={(tab) => setPanel((p) => ({ ...p, tab }))}
                      onClose={() => setPanel((p) => ({ ...p, open: false }))}
                      problems={problems}
                      onProblem={(p) => openFile(p.path, p.line)}
                      output={output}
                      console={consoleLines}
                      terminalUrl={ready ? sb.agent.terminalUrl(sb.id!, 100, 30) : null}
                      terminalNote={sb.error}
                      dark={colourScheme === 'dark'}
                      onClear={() => (panel.tab === 'output' ? setOutput([]) : setConsoleLines([]))}
                    />
                  </Pane>
                </>
              )}
            </Group>
          </Pane>
        </Group>
      </div>

      {/* status bar */}
      <footer className="flex h-6 shrink-0 items-center gap-3 overflow-hidden bg-primary px-2 text-[11.5px] text-white" data-testid="pg-statusbar">
        <span className="flex items-center gap-1"><GitBranch size={12} /> {selected.git.branch}</span>
        <button type="button" className="flex items-center gap-1 hover:underline" onClick={() => show('problems')} aria-label={`${count.errors} errors, ${count.warnings} warnings`}>⊗ {count.errors} ⚠ {count.warnings}</button>
        <span className="hidden min-w-0 items-center gap-1 sm:flex"><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${sb.status === 'ready' ? 'bg-emerald-300' : sb.status === 'opening' ? 'bg-amber-300' : 'bg-red-300'}`} /> <span className="truncate" title={sb.error ?? undefined} data-testid="pg-sandbox-state">Sandbox {sbLabel}{sb.status === 'error' && sb.error ? `: ${sb.error}` : ''}{sb.info?.container ? ` · ${sb.info.container}` : ''} · {environment}</span></span>
        {(sb.status === 'error' || sb.status === 'offline') && <button type="button" className="shrink-0 font-semibold underline" onClick={() => void sb.open()} data-testid="pg-sandbox-retry">Retry</button>}
        {!panel.open && <button type="button" className="hover:underline" onClick={() => show('console')}>Show panel</button>}
        <span className="flex-1" />
        <span>Ln {cursor.line}, Col {cursor.col}</span>
        <span className="hidden md:inline">Spaces: 2</span>
        <span className="hidden md:inline">UTF-8</span>
        <span className="hidden md:inline">LF</span>
        <span>{language}</span>
        <span className="rounded bg-white/20 px-1.5">{STATUS_LABEL[selected.status]}</span>
        <span className="hidden sm:inline">{user.role}</span>
      </footer>

      <NewFileDialog files={files} isOpen={newFile !== null} folder={newFile ?? ''} exists={exists} onClose={() => setNewFile(null)} onCreate={(p, k, t) => void createFile(p, k, t)} />
      <PromptDialog
        isOpen={newFolder !== null}
        onClose={() => setNewFolder(null)}
        title="New folder"
        message="Kept by a .gitkeep file until a file goes in (git keeps no empty folders)."
        inputLabel="Folder path"
        initialValue={newFolder ? `${newFolder}/` : ''}
        validate={(v) => { const p = v.replace(/\/$/, ''); return !p ? 'Type a name' : !isValidPath(p) ? 'Letters, numbers, - _ . and / only' : exists(p) ? `${p} already exists` : null }}
        confirmText="Create folder"
        onConfirm={(v) => void createFolder(v.replace(/\/$/, ''))}
      />
      <PromptDialog
        isOpen={renaming !== null}
        onClose={() => setRenaming(null)}
        title={`Rename or move ${renaming ?? ''}`}
        message="Type the new path; a folder moves with everything in it. An endpoint's route follows its file; test events follow the route."
        inputLabel="New path"
        initialValue={renaming ?? ''}
        validate={(v) => (!isValidPath(v) ? 'Letters, numbers, - _ . and / only' : v === renaming ? 'Same as now' : exists(v) ? `${v} already exists` : null)}
        confirmText="Rename"
        onConfirm={(v) => { if (renaming) void rename(renaming, v) }}
      />
      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        type="danger"
        title={`Delete ${deleting ?? ''}?`}
        message={deleting ? `${files.filter((f) => f.path === deleting || f.path.startsWith(deleting + '/')).length} file(s) and the test events of their endpoints leave the working copy; Save commits it. The API records in 3.1 stay (mark them old there).` : ''}
        confirmText="Delete"
        onConfirm={() => { if (deleting) void remove(deleting) }}
      />
      <TestEventDrawer
        isOpen={newTestFor !== null}
        onClose={() => { setNewTestFor(null); setTestFrom(null) }}
        initial={testFrom}
        endpoint={newTestFor ?? ''}
        outcomes={localManifest.endpoints.find((e) => e.path === newTestFor)?.outcomes ?? []}
        onAdd={(t) => {
          change(files, [...tests, { ...t, id: newTestId() }])
          print([`[${stamp()}] test event “${t.name}” → ${t.expectedOutcome} added for ${endpointLabel(t.endpoint)} (working copy; Save commits it)`])
        }}
      />
      <ConfirmDialog isOpen={confirmLive} onClose={() => setConfirmLive(false)} type="info" title={`Make ${selected.version} live?`} message={`The runner starts image hub-batch-${codeKeyOf(selected).replace(/\./g, '-')}:${selected.version} with the production variables from the vault (checksum checked). Live workflows on the current live batch move to ${selected.version} after the C1 check; the current live version becomes previous.`} confirmText="Make live" onConfirm={async () => { setConfirmLive(false); print(await makeLive(selected)); show('output') }} />
      <ConfirmDialog isOpen={confirmProd} onClose={() => setConfirmProd(false)} type="danger" title="Switch the sandbox to production?" message={`.env gets the production values of ${lender.name}; a run with “real lender” then reaches the production API and the lender receives a real test lead. Use the lender's test applicant. Recorded in History.`} confirmText="Switch to production" onConfirm={() => { setEnvironment('production'); if (!apiMode()) useHubStore.getState().addHistory(user.email, lender.slug, `Playground ${selected.version}`, 'Sandbox switched to production (real calls with production keys)'); setConfirmProd(false) }} />
      <ConfirmDialog isOpen={confirmReseed} onClose={() => setConfirmReseed(false)} type="danger" title="Re-seed the sandbox?" message="The sandbox folder is replaced by the Hub's copy of this version (the last Save). Files changed in the sandbox but not saved are lost; node_modules too." confirmText="Re-seed" onConfirm={async () => { setConfirmReseed(false); await sb.open(true); notify.info('Sandbox re-seeded') }} />
      <PromptDialog
        isOpen={newVersionOpen}
        onClose={() => setNewVersionOpen(false)}
        title="New version from this"
        message={`A copy of ${selected.version} (${selected.files.length} files, ${selected.manifest.endpoints.length} endpoints, ${selected.tests.length} test events) as a draft on its own git branch.`}
        inputLabel="Version number"
        initialValue={nextVersion(mine)}
        validate={(v) => (isValidVersion(v) ? (mine.some((b) => b.version === v) ? `${v} already exists` : null) : 'Must look like 1.2.0')}
        confirmText="Create"
        onConfirm={(v) => {
          const b = newVersion(lender.slug, selected.id, v)
          setNewVersionOpen(false)
          if (b) setParams((p) => (p.set('v', b.version), p.set('p', b.productSlug ?? 'lender'), p.delete('file'), p))
        }}
      />
    </div>
  )
}
